/* istanbul ignore file */
import { makeFragmentData } from '@packages/gql/generated/shopping';
import {
  DownloadFragmentFragmentDoc,
  ProductPriceFragmentFragmentDoc,
} from '@packages/gql/generated/shopping/graphql';
import type { EfficiencyFlagProps } from '@packages/shared';
import { Box, EfficiencyFlag, Image, Typography, Skeleton } from '@packages/shared';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { Download } from '../../../Download';
import { Price } from '../../../Price';
import { ServiceItems } from '../ServiceItems/ServiceItems';
import { LegacyEnergyFlag } from './LegacyEnergyFlag';
import { useBasketData } from './useBasketData';
import { useFormatedPrice } from '../../../../hooks/useFormatedPrice/useFormatedPrice';

export const AddedProductLoadingFallback = () => (
  <Box sx={{ display: 'flex', gap: 2 }}>
    <Skeleton variant="rectangular" width={120} height={120} />
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Skeleton variant="rectangular" width="100%" height="20px" />
      <Skeleton variant="rectangular" width="100%" height="20px" />
      <Skeleton variant="rectangular" width="120px" height="20px" />
      <Skeleton variant="rectangular" width="120px" height="20px" />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignSelf: 'flex-end' }}>
        <Skeleton variant="rectangular" width="100px" height="20px" />
        <Skeleton variant="rectangular" width="100px" height="20px" />
      </Box>
    </Box>
  </Box>
);

// TODO: check for mapper
const mapTireEfficiency = (text?: string): EfficiencyFlagProps['type'] => {
  switch (text) {
    case 'Nasshaftung':
      return 'wetGrip';
    case 'Rollgeraeusch':
      return 'externalRollingNoise';
    default:
      return 'fuelEfficiency';
  }
};

// TODO(api-layer): needs to be refactored with gql fragments when basket resolvers are implemented
export const AddedProduct = ({ lineItems }: { lineItems?: Array<string> }) => {
  const { formatMessage } = useIntl();
  const formatPrice = useFormatedPrice('ATB');

  const newLineItem = (lineItems && lineItems.length > 0 && lineItems[0]) || null;
  const { data } = useBasketData(newLineItem);
  if (!data) {
    return null;
  }

  const product = data.products.find((p) => p.lineItemId === newLineItem);

  if (!product) {
    return null;
  }
  const {
    imageUrl,
    name,
    variation,
    deliveryMessage,
    deliveryTextColor,
    price,
    quantity,
    singlePrice,
    powerEfficiency,
    aplus,
  } = product;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box
          sx={{ flexShrink: 0, position: 'relative', width: [80, 80, 120], height: [80, 80, 120] }}
        >
          <Image src={imageUrl} style={{ objectFit: 'contain' }} fill alt="" sizes="25vw" />
        </Box>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              typography: ['h6', 'h6', 'h5'],
              fontWeight: ['fontWeightSemiBold', 'fontWeightSemiBold', 'normal'],
            }}
          >
            {name}
          </Typography>
          <Typography variant="body2">{variation}</Typography>
          <Box>
            <Typography variant="body2">
              {formatMessage(
                {
                  id: 'atblayer.singleprice.label',
                  defaultMessage: 'Anzahl {quantity} (á {singlePrice})',
                },
                {
                  quantity,
                  singlePrice: formatPrice(singlePrice.value, singlePrice.currencyCode),
                },
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: `${[deliveryTextColor]}.main` }}>
              {deliveryMessage}
            </Typography>
          </Box>
          <Box
            sx={{ display: 'flex', flexDirection: ['column', 'column', 'row'], gap: 1, mt: 1.5 }}
          >
            <Box sx={{ flexGrow: 1 }}>
              {powerEfficiency && powerEfficiency.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {powerEfficiency.map((p, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Fragment key={i}>
                        {p.iconText ? (
                          <EfficiencyFlag
                            text={p.iconText!}
                            overlay={{ title: p.text || '', imgUrl: p.link }}
                            type={mapTireEfficiency(p.text) as EfficiencyFlagProps['type']}
                          />
                        ) : (
                          <LegacyEnergyFlag
                            icon={`${p.icon}.svg`}
                            overlayImg={p.link}
                            overlayTitle={p.text}
                          />
                        )}
                      </Fragment>
                    ))}
                  </Box>
                  {powerEfficiency[0].dataSheet && (
                    <Download
                      downloadData={makeFragmentData(
                        { link: powerEfficiency[0].dataSheet, type: 'PRODUCT_DATASHEET' },
                        DownloadFragmentFragmentDoc,
                      )}
                      renderIcon={false}
                    />
                  )}
                </>
              )}
            </Box>
            <Price
              priceData={makeFragmentData(
                {
                  ...price,
                },
                ProductPriceFragmentFragmentDoc,
              )}
              showLegalHints
              alignment="right"
              small
            />
          </Box>
        </Box>
      </Box>
      {aplus && newLineItem && <ServiceItems serviceItems={aplus} pli={newLineItem} />}
    </Box>
  );
};
